/**
 * Search service layer.
 * Provides unified search across users, posts, workouts, and hashtags.
 *
 * NOTE: Firestore does not support full-text search natively. This service
 * uses prefix matching for usernames and exact matching for tags. Post text
 * search fetches recent posts and filters client-side. For a production app,
 * consider Algolia or Typesense for full-text search.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  where,
  serverTimestamp,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const POSTS_COLLECTION = 'posts';
const USERS_COLLECTION = 'users';
const TAGS_COLLECTION = 'tags';

const RECENT_SEARCHES_KEY = 'fitsocial_recent_searches';
const MAX_RECENT_SEARCHES = 5;

// ── Users search ────────────────────────────────────────────────────────────

/**
 * Search users by username prefix and display name.
 * @param {string} queryStr
 * @param {number} [resultLimit=10]
 * @returns {Promise<Array<Object>>}
 */
export const searchUsers = async (queryStr, resultLimit = 10) => {
  try {
    const normalized = queryStr.toLowerCase().trim();
    if (!normalized) return [];

    const usersRef = collection(db, USERS_COLLECTION);

    // Search by username prefix
    const usernameQuery = query(
      usersRef,
      where('username', '>=', normalized),
      where('username', '<=', normalized + '\uf8ff'),
      firestoreLimit(resultLimit)
    );

    const usernameSnapshot = await getDocs(usernameQuery);
    const results = new Map();

    usernameSnapshot.docs.forEach((docSnap) => {
      results.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
    });

    // Also search by displayName (client-side filter for simplicity)
    if (results.size < resultLimit) {
      const allUsersQuery = query(
        usersRef,
        orderBy('displayName'),
        firestoreLimit(50)
      );
      const allUsersSnapshot = await getDocs(allUsersQuery);

      allUsersSnapshot.docs.forEach((docSnap) => {
        if (results.size >= resultLimit) return;
        const data = docSnap.data();
        const displayNameLower = (data.displayName || '').toLowerCase();
        const bioLower = (data.bio || '').toLowerCase();
        if (
          (displayNameLower.includes(normalized) || bioLower.includes(normalized)) &&
          !results.has(docSnap.id)
        ) {
          results.set(docSnap.id, { id: docSnap.id, ...data });
        }
      });
    }

    return Array.from(results.values()).slice(0, resultLimit);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Search users]', error);
    }
    return [];
  }
};

// ── Posts search ─────────────────────────────────────────────────────────────

/**
 * Serialize a Firestore post document into a plain object.
 */
const serializePost = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    type: data.type || 'text',
    text: data.text || '',
    imageUrl: data.imageUrl || null,
    authorId: data.authorId || '',
    authorName: data.authorName || '',
    authorUsername: data.authorUsername || '',
    authorAvatarUrl: data.authorAvatarUrl || null,
    likesCount: data.likesCount || 0,
    commentsCount: data.commentsCount || 0,
    edited: data.edited || false,
    tags: data.tags || [],
    mentions: data.mentions || [],
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt instanceof Timestamp
      ? data.updatedAt.toDate().toISOString()
      : data.updatedAt || null,
    workout: data.workout || null,
    meal: data.meal || null,
    progress: data.progress || null,
    _docSnapshot: docSnap,
  };
};

/**
 * Search posts by text content. Fetches recent posts and filters client-side.
 * @param {string} queryStr
 * @param {number} [resultLimit=10]
 * @param {*} [lastDoc=null]
 * @returns {Promise<{ posts: Array<Object>, lastDoc: *, hasMore: boolean }>}
 */
export const searchPosts = async (queryStr, resultLimit = 10, lastDoc = null) => {
  try {
    const normalized = queryStr.toLowerCase().trim();
    if (!normalized) return { posts: [], lastDoc: null, hasMore: false };

    const postsRef = collection(db, POSTS_COLLECTION);
    const fetchLimit = resultLimit * 5; // Fetch more to filter

    let q;
    if (lastDoc) {
      q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(fetchLimit)
      );
    } else {
      q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        firestoreLimit(fetchLimit)
      );
    }

    const snapshot = await getDocs(q);
    const matched = [];
    let lastVisible = null;

    for (const docSnap of snapshot.docs) {
      lastVisible = docSnap;
      const data = docSnap.data();
      const text = (data.text || '').toLowerCase();
      const authorName = (data.authorName || '').toLowerCase();
      const authorUsername = (data.authorUsername || '').toLowerCase();

      if (
        text.includes(normalized) ||
        authorName.includes(normalized) ||
        authorUsername.includes(normalized)
      ) {
        matched.push(serializePost(docSnap));
        if (matched.length >= resultLimit) break;
      }
    }

    const hasMore = snapshot.docs.length === fetchLimit;
    return { posts: matched, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Search posts', 'Failed to search posts.');
  }
};

// ── Workouts search ──────────────────────────────────────────────────────────

/**
 * Search workout posts by workout title.
 * @param {string} queryStr
 * @param {string} [userId] - Optional user filter.
 * @param {number} [resultLimit=10]
 * @returns {Promise<Array<Object>>}
 */
export const searchWorkouts = async (queryStr, userId = null, resultLimit = 10) => {
  try {
    const normalized = queryStr.toLowerCase().trim();
    if (!normalized) return [];

    const postsRef = collection(db, POSTS_COLLECTION);
    const constraints = [
      where('type', '==', 'workout'),
      orderBy('createdAt', 'desc'),
      firestoreLimit(resultLimit * 5),
    ];

    if (userId) {
      constraints.splice(1, 0, where('authorId', '==', userId));
    }

    const q = query(postsRef, ...constraints);
    const snapshot = await getDocs(q);
    const matched = [];

    for (const docSnap of snapshot.docs) {
      if (matched.length >= resultLimit) break;
      const data = docSnap.data();
      const title = (data.workout?.title || '').toLowerCase();
      const text = (data.text || '').toLowerCase();
      if (title.includes(normalized) || text.includes(normalized)) {
        matched.push(serializePost(docSnap));
      }
    }

    return matched;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Search workouts]', error);
    }
    return [];
  }
};

// ── Tags ─────────────────────────────────────────────────────────────────────

/**
 * Search posts by hashtag.
 * @param {string} tag - Tag name without #, lowercase.
 * @param {number} [resultLimit=10]
 * @param {*} [lastDoc=null]
 * @returns {Promise<{ posts: Array<Object>, lastDoc: *, hasMore: boolean }>}
 */
export const searchByTag = async (tag, resultLimit = 10, lastDoc = null) => {
  try {
    const normalized = tag.toLowerCase().trim();
    if (!normalized) return { posts: [], lastDoc: null, hasMore: false };

    const postsRef = collection(db, POSTS_COLLECTION);
    let q;

    if (lastDoc) {
      q = query(
        postsRef,
        where('tags', 'array-contains', normalized),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(resultLimit)
      );
    } else {
      q = query(
        postsRef,
        where('tags', 'array-contains', normalized),
        orderBy('createdAt', 'desc'),
        firestoreLimit(resultLimit)
      );
    }

    const snapshot = await getDocs(q);
    const posts = snapshot.docs.map(serializePost);
    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    const hasMore = snapshot.docs.length === resultLimit;

    return { posts, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Search by tag', 'Failed to search by tag.');
  }
};

/**
 * Get trending tags sorted by post count.
 * @param {number} [resultLimit=12]
 * @returns {Promise<Array<{ name: string, postCount: number, lastUsed: string }>>}
 */
export const getTrendingTags = async (resultLimit = 12) => {
  try {
    const tagsRef = collection(db, TAGS_COLLECTION);
    const q = query(
      tagsRef,
      orderBy('postCount', 'desc'),
      firestoreLimit(resultLimit)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        name: data.name || docSnap.id,
        postCount: data.postCount || 0,
        lastUsed: data.lastUsed instanceof Timestamp
          ? data.lastUsed.toDate().toISOString()
          : data.lastUsed || null,
      };
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Get trending tags]', error);
    }
    return [];
  }
};

/**
 * Search tags by prefix.
 * @param {string} queryStr
 * @param {number} [resultLimit=10]
 * @returns {Promise<Array<{ name: string, postCount: number }>>}
 */
export const searchTags = async (queryStr, resultLimit = 10) => {
  try {
    const normalized = queryStr.toLowerCase().trim();
    if (!normalized) return [];

    const tagsRef = collection(db, TAGS_COLLECTION);
    const q = query(
      tagsRef,
      where('name', '>=', normalized),
      where('name', '<=', normalized + '\uf8ff'),
      orderBy('name'),
      firestoreLimit(resultLimit)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        name: data.name || docSnap.id,
        postCount: data.postCount || 0,
      };
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Search tags]', error);
    }
    return [];
  }
};

/**
 * Update tag counts in the tags collection when a post is created.
 * @param {string[]} tags - Array of lowercase tag names.
 * @returns {Promise<void>}
 */
export const incrementTagCounts = async (tags) => {
  try {
    if (!tags || tags.length === 0) return;

    for (const tag of tags) {
      const tagRef = doc(db, TAGS_COLLECTION, tag);
      const tagSnap = await getDoc(tagRef);

      if (tagSnap.exists()) {
        await setDoc(tagRef, {
          postCount: increment(1),
          lastUsed: serverTimestamp(),
        }, { merge: true });
      } else {
        await setDoc(tagRef, {
          name: tag,
          postCount: 1,
          lastUsed: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Increment tag counts]', error);
    }
  }
};

/**
 * Decrement tag counts when a post is deleted.
 * @param {string[]} tags
 * @returns {Promise<void>}
 */
export const decrementTagCounts = async (tags) => {
  try {
    if (!tags || tags.length === 0) return;

    for (const tag of tags) {
      const tagRef = doc(db, TAGS_COLLECTION, tag);
      await setDoc(tagRef, {
        postCount: increment(-1),
      }, { merge: true });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Decrement tag counts]', error);
    }
  }
};

// ── Recent searches (localStorage) ──────────────────────────────────────────

/**
 * Get recent searches from localStorage.
 * @returns {string[]}
 */
export const getRecentSearches = () => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Save a search query to recent searches.
 * @param {string} queryStr
 */
export const saveRecentSearch = (queryStr) => {
  try {
    const trimmed = queryStr.trim();
    if (!trimmed) return;

    const recent = getRecentSearches().filter((s) => s !== trimmed);
    recent.unshift(trimmed);
    localStorage.setItem(
      RECENT_SEARCHES_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT_SEARCHES))
    );
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Clear recent searches.
 */
export const clearRecentSearches = () => {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    // Ignore
  }
};
